import React, { Component } from "react";
import * as Icon from "react-feather";

import "./quickbooks-journal-modal.css";
import ToolsModal from "../../../kit/ToolsModal";
import ToolsSelect from "../../../kit/ToolsSelect";
import ToolsAcknowledgeNotice from "../../../kit/ToolsAcknowledgeNotice";
import UtilityService from "../../../../../../services/UtilityService";

class QuickBooksJournalModal extends Component {
	renderAccountOfLine = line => {
		if (line.isLinked) return <span className="qbjm__quickbooks-name">{line.quickBooksName}</span>;
		const needsAChoice = line.candidates.length > 0;
		if (needsAChoice) {
			return (
				<ToolsSelect
					value={line.chosen || null}
					options={line.candidates}
					onChange={value => this.props.onChooseCandidate({ lineKey: line.key, value })}
					placeholder={`Choose the QuickBooks account for ${line.chartName}`}
					inline
				/>
			);
		}
		return <span className="qbjm__placeholder" data-test="qbo-journal-line-placeholder">{line.placeholder}</span>;
	};

	renderLine = line => {
		return (
			<div key={line.key} className="tk-ledger__row qbjm__line" data-test="qbo-journal-line">
				<span className="qbjm__account">
					{this.renderAccountOfLine(line)}
					{line.chartName !== line.quickBooksName && <span className="qbjm__chart-name">{line.chartName}</span>}
				</span>
				<span className="tk-ledger__amount">{line.debit ? UtilityService.formatCurrency(line.debit) : ""}</span>
				<span className="tk-ledger__amount">{line.credit ? UtilityService.formatCurrency(line.credit) : ""}</span>
			</div>
		);
	};

	renderOutcome = () => {
		const { bookedAs } = this.props;
		return (
			<div className="tk-notice tk-notice--success" data-test="qbo-journal-booked">
				<div className="tk-notice__head">
					<span className="tk-notice__title"><Icon.CheckCircle size={14} /> Booked in QuickBooks as journal {bookedAs.qboJournalEntryId}</span>
				</div>
				<p className="tk-notice__detail">
					The tool keeps applying it on top of the pulled figures until the next pull is saved; then QuickBooks carries it and it is not counted
					twice. What was sent and QuickBooks's reply are kept with the file's imported files.
				</p>
			</div>
		);
	};

	renderFooter = () => {
		const { bookedAs, booking, canBook, onBook, onClose } = this.props;
		if (bookedAs) return <span className="tk-modal-actions"><button type="button" className="tk-button tk-button--primary" onClick={onClose}>Done</button></span>;
		return (
			<span className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={onClose} disabled={booking}>Cancel</button>
				<button type="button" className="tk-button tk-button--primary" data-test="qbo-journal-confirm" onClick={onBook} disabled={!canBook || booking}>
					{booking ? "Booking…" : "Book in QuickBooks"}
				</button>
			</span>
		);
	};

	renderWarning = warning => {
		const { acknowledged, onAcknowledgeWarning, bookedAs } = this.props;
		const isAcknowledged = !!(acknowledged && acknowledged[warning.code]);
		const canStillAcknowledge = !bookedAs && !!onAcknowledgeWarning;
		return (
			<ToolsAcknowledgeNotice
				key={warning.code + warning.message}
				message={warning.message}
				checked={isAcknowledged}
				onChange={canStillAcknowledge ? event => onAcknowledgeWarning({ code: warning.code, checked: event.target.checked }) : undefined}
				label="I've read this and want to book it anyway"
				dataTest="qbo-journal-warning"
			/>
		);
	};

	render() {
		const {
			open,
			onClose,
			title,
			company,
			txnDate,
			memo,
			lines,
			totals,
			refusals,
			warnings,
			error,
			bookedAs,
			children,
			note = "This writes a journal entry into the client's QuickBooks company. It can be removed again from here until the file is signed off.",
		} = this.props;
		const context = (
			<span className="qbjm__context">
				<span className="qbjm__company">{company || "QuickBooks company"}</span>
				<span className="qbjm__date">Dated {UtilityService.formatDate({ value: txnDate })}</span>
			</span>
		);
		return (
			<ToolsModal open={open} onClose={onClose} title={title} context={context} width={680} footer={this.renderFooter()}>
				<div className="qbjm" data-test="qbo-journal-modal">
					{bookedAs && this.renderOutcome()}
					{error && <div className="tk-notice tk-notice--danger"><p className="tk-notice__detail">{error}</p></div>}
					{refusals.length > 0 && (
						<div className="tk-notice tk-notice--danger" data-test="qbo-journal-refusals">
							<div className="tk-notice__head"><span className="tk-notice__title">This cannot be booked yet</span></div>
							{refusals.map(refusal => <p key={refusal} className="tk-notice__detail">{refusal}</p>)}
						</div>
					)}
					{warnings.map(this.renderWarning)}
					{children}
					<div className="qbjm__table">
						<div className="tk-ledger__row tk-ledger__row--head qbjm__line">
							<span>QuickBooks account</span>
							<span className="tk-ledger__amount">Debit</span>
							<span className="tk-ledger__amount">Credit</span>
						</div>
						{lines.map(this.renderLine)}
						<div className="tk-ledger__row tk-ledger__row--total qbjm__line">
							<span>Total</span>
							<span className="tk-ledger__amount">{UtilityService.formatCurrency(totals.debits)}</span>
							<span className="tk-ledger__amount">{UtilityService.formatCurrency(totals.credits)}</span>
						</div>
					</div>
					<p className="qbjm__memo"><span className="qbjm__memo-label">Memo</span> {memo}</p>
					{!bookedAs && <p className="qbjm__note">{note}</p>}
				</div>
			</ToolsModal>
		);
	}
}

export default QuickBooksJournalModal;
